import type { PlanCue } from '@toolproof-core/lib/_contract/scene-plan';

export type NarrationTimepoint = { markName: string; timeSeconds: number };
export type SubtitleSentence = { text: string; start: number };
/** Pairs a time with the narration text offset spoken at that time. */
export type TimelineAnchor = { seconds: number; offset: number };
type TimelineCue = Pick<PlanCue, 'id' | 'beforeText'>;
type Pause = { offset: number; milliseconds: number };

/** Explicit pauses add time without stealing from the authored reading duration. */
export function readingPositionSeconds(text: string, offset: number, durationSeconds: number, pauses: readonly Pause[] = []): number {
    return durationSeconds * offset / text.length
        + pauses.filter(pause => pause.offset <= offset).reduce((sum, pause) => sum + pause.milliseconds / 1000, 0);
}

export function splitSubtitleSentences(text: string): readonly SubtitleSentence[] {
    return [...text.matchAll(/\S[\s\S]*?(?:[.!?]+["'”’)\]]*(?=\s|$)|$)/g)]
        .map((match) => ({ text: match[0], start: match.index }));
}

/** Without a recording, cues are timed in proportion to their place in the text. */
export function readingTimepoints(text: string, cues: readonly TimelineCue[], durationSeconds: number,
    pauses: readonly Pause[] = []): readonly NarrationTimepoint[] {
    return cues.map((cue) => ({ markName: cue.id,
        timeSeconds: readingPositionSeconds(text, text.indexOf(cue.beforeText), durationSeconds, pauses) }));
}

export function readingTimelineAnchors(text: string, cues: readonly TimelineCue[], durationSeconds: number,
    pauses: readonly Pause[] = []): readonly TimelineAnchor[] {
    if (!pauses.length) {
        return narrationTimelineAnchors(text, cues, readingTimepoints(text, cues, durationSeconds), durationSeconds);
    }
    return [
        { seconds: 0, offset: 0 },
        ...cues.map(cue => {
            const offset = text.indexOf(cue.beforeText);
            return { seconds: readingPositionSeconds(text, offset, durationSeconds, pauses), offset };
        }),
        ...pauses.flatMap(pause => {
            const end = readingPositionSeconds(text, pause.offset, durationSeconds, pauses);
            return [{ seconds: end - pause.milliseconds / 1000, offset: pause.offset },
                { seconds: end, offset: pause.offset }];
        }),
        { seconds: readingPositionSeconds(text, text.length, durationSeconds, pauses), offset: text.length },
    ].sort((a, b) => a.seconds - b.seconds || a.offset - b.offset);
}

/**
 * Known times are the start, each cue (or, when recorded, each word's start) and the end; text progress between them
 * is assumed steady. Recordings mark only word starts, so a word is assumed to last as long as the clip's average
 * per character, ending earlier if the next word starts first; pauses then hold progress rather than slowing it.
 */
export function narrationTimelineAnchors(text: string, cues: readonly TimelineCue[],
    timepoints: readonly NarrationTimepoint[], durationSeconds: number,
    wordBoundaries?: readonly number[]): readonly TimelineAnchor[] {
    const anchors: TimelineAnchor[] = [{ seconds: 0, offset: 0 }];
    const add = (seconds: number, offset: number) => {
        const previous = anchors.at(-1)!;
        if (seconds > previous.seconds && offset >= previous.offset) anchors.push({ seconds, offset });
    };
    const words = [...text.matchAll(/\S+/g)];
    if (wordBoundaries && words.length && wordBoundaries.length === words.length + 1) {
        const characters = words.reduce((total, word) => total + word[0].length, 0);
        const perCharacter = (wordBoundaries.at(-1)! - wordBoundaries[0]!) / characters;
        words.forEach((word, index) => {
            const start = wordBoundaries[index]!;
            add(start, word.index);
            add(Math.min(wordBoundaries[index + 1]!, start + word[0].length * perCharacter), word.index + word[0].length);
        });
    } else {
        cues.forEach((cue, index) => {
            const point = timepoints[index];
            if (point) add(point.timeSeconds, text.indexOf(cue.beforeText));
        });
    }
    const last = anchors.at(-1)!;
    anchors.push({ seconds: Math.max(durationSeconds, last.seconds + 1), offset: text.length });
    return anchors;
}

function interpolate(anchors: readonly TimelineAnchor[], value: number, from: 'seconds' | 'offset', to: 'seconds' | 'offset'): number {
    const after = anchors.findIndex((anchor) => anchor[from] > value);
    if (after <= 0) return after === 0 ? anchors[0]![to] : anchors.at(-1)![to];
    const a = anchors[after - 1]!;
    const b = anchors[after]!;
    return a[to] + (b[to] - a[to]) * (value - a[from]) / (b[from] - a[from]);
}

/** Rebase speech progress onto authored display fragments, including numbers spoken as several words. */
export function displayTimelineAnchors(anchors: readonly TimelineAnchor[],
    map: readonly { spokenOffset: number; displayOffset: number }[]): readonly TimelineAnchor[] {
    const mapping = map.map(point => ({ seconds: point.spokenOffset, offset: point.displayOffset }));
    const boundaries = map.map(point => ({ seconds: interpolate(anchors, point.spokenOffset, 'offset', 'seconds'),
        offset: point.spokenOffset }));
    return [...anchors, ...boundaries].sort((a, b) => a.seconds - b.seconds || a.offset - b.offset)
        .map(anchor => ({ seconds: anchor.seconds, offset: interpolate(mapping, anchor.offset, 'seconds', 'offset') }));
}

export function subtitleSentenceAt(sentences: readonly SubtitleSentence[], anchors: readonly TimelineAnchor[], seconds: number): number {
    const offset = interpolate(anchors, seconds, 'seconds', 'offset');
    let index = 0;
    while (index + 1 < sentences.length && sentences[index + 1]!.start <= offset) index++;
    return index;
}

/** The sentence being spoken and how many of its characters have been spoken, estimated between known times. */
export function spokenSentenceProgress(sentences: readonly SubtitleSentence[], anchors: readonly TimelineAnchor[],
    seconds: number): { index: number; text: string; offset: number } | undefined {
    const index = subtitleSentenceAt(sentences, anchors, seconds);
    const sentence = sentences[index];
    if (!sentence) return undefined;
    const offset = interpolate(anchors, seconds, 'seconds', 'offset') - sentence.start;
    return { index, text: sentence.text, offset: Math.min(sentence.text.length, Math.max(0, offset)) };
}

/** When each sentence after the first begins, for timelines driven by a clock rather than an audio position. */
export function subtitleSentenceStarts(sentences: readonly SubtitleSentence[], anchors: readonly TimelineAnchor[]): readonly number[] {
    return sentences.map((sentence) => interpolate(anchors, sentence.start, 'offset', 'seconds'));
}
